import React, {
  useState,
  useCallback,
  useEffect,
  useMemo,
  useRef,
} from "react";
import { useTranslation } from "react-i18next";
import { MeetingType } from "../../../../services/meetingTypeService";
import { useBookingPage } from "../../BookingPageContext";
import { useAuth } from "../../../../contexts/AuthContext";
import { effectiveAccessRequirement } from "../../../../utils/meetingTypeAccess";
import { PublicProduct, PublicProvider } from "../../../../types/product";
import CalendarStep from "./CalendarStep";
import TimeSlotDisplay from "./TimeSlotDisplay";
import BookingFormStep from "./BookingFormStep";
import UserAvatar from "../../../../components/common/UserAvatar";
import BorderlessTimezoneSelector from "../../../../components/common/BorderlessTimezoneSelector";
import HostDisplay from "../../../../components/HostDisplay";
import SocialMediaLinks from "../../../../components/SocialMediaLinks";
import BrandingSash from "../../../../components/BrandingSash";
import ServiceSummaryPanel from "./ServiceSummaryPanel";
import LoadingSpinner from "../../../../components/core/LoadingSpinner";
import { ErrorBanner } from "../../../../components/core/ErrorBanner";
import { useBookingSubmission } from "../../hooks/useBookingSubmission";
import PlanCheckoutScreen from "../PlanCheckout";
import ServiceEntitlementGuard from "../../../../components/customer/ServiceEntitlementGuard";
import bookingService, { WaitlistClaimInfo } from "../../../../services/bookingService";
import { getPurchaseGrant, clearPurchaseGrant } from "../../../../services/customerService";
import { extractSystemFields } from "../../../../utils/formFieldExtractor";
import { Dropdown } from "../../../../components/ui";
// NORTH STAR: Month key generation moved to backend Event Store system
import { TimeSlot } from "../../../../types/booking";
import { generateCacheKey } from "../../../../utils/cacheKeyGenerator";
import { TIMEZONES } from "../../../../utils/timezones";
import { useBookingData } from "../../../../providers/BookingDataProvider";
import { trackBookingSlotSelected } from "../../../../services/analyticsTrackingService";
import { format } from "date-fns";

// Helper function to format timezone display (GMT offset + city name)
const formatTimezoneDisplay = (timezone: string): string => {
  const timezoneInfo = TIMEZONES.find(tz => tz.value === timezone);
  if (timezoneInfo) {
    return timezoneInfo.label;
  }

  // Fallback: try to extract city name and get GMT offset
  try {
    const cityName = timezone.split('/').pop()?.replace(/_/g, ' ') || timezone;
    const now = new Date();
    const utcOffset = new Date(now.toLocaleString('en-US', { timeZone: 'UTC' })).getTime() -
                     new Date(now.toLocaleString('en-US', { timeZone: timezone })).getTime();
    const offsetHours = Math.round(utcOffset / (1000 * 60 * 60));
    const sign = offsetHours >= 0 ? '+' : '-';
    const formattedOffset = `GMT${sign}${Math.abs(offsetHours).toString().padStart(2, '0')}:00`;
    return `${formattedOffset} ${cityName}`;
  } catch (error) {
    return timezone; // Fallback to original timezone string
  }
};

interface BookingFlowProps {
  meetingType: MeetingType | null;
  /**
   * The host this page belongs to. Only used to build the link to their shop
   * when a booking is refused for want of points — absent in preview and
   * reschedule, where there is no shop trip to offer.
   */
  username?: string;
  onBookingComplete: () => void;
  mode?: "booking" | "reschedule" | "preview";
  rescheduleToken?: string;
  originalBookingData?: {
    startTime: Date;
    endTime: Date;
    duration?: number; // Original booking duration in minutes
    guestName: string;
    guestEmail: string;
    notes?: string;
    timeZone: string;
    formSubmissionData?: Record<string, any>;
  };
  onPreviewRedirect?: (url: string) => void;
  /** Service booking selection (for service type meetings) */
  serviceSelection?: {
    service: PublicProduct | null; // Can be null in duration mode (no service options)
    provider: PublicProvider | null;
    isNoPreference: boolean;
    effectiveDuration: number;
    effectivePrice: number;
  } | null;
  /** Callback to return to service/provider selection (for service types) */
  onBackToServiceFlow?: () => void;
  /** Real meeting type ID for preview mode (for fetching capacity data) */
  previewRealMeetingTypeId?: number | null;
}

/**
 * Format an amount + ISO 4217 currency for display on the booking page.
 * Phase 2 — used by the "Amount due" row below the meeting location.
 */
const BOOKING_ZERO_DECIMAL_CCY = new Set(['BIF','CLP','DJF','GNF','JPY','KMF','KRW','MGA','PYG','RWF','UGX','VND','VUV','XAF','XOF','XPF']);
/**
 * Points priced like a currency. `formatBookingAmount` takes the unit so a
 * points-priced meeting type reads "5 points" everywhere an amount would
 * otherwise read "¥5,000" — there is exactly one formatter feeding the
 * amount-due row and the duration menu, so this is the only place to teach.
 *
 * Never routed through Intl.NumberFormat: points are not an ISO currency and
 * asking for one would throw or invent a symbol.
 */
function formatBookingPoints(amount: number | undefined, pointsLabel: string): string {
  return `${Number(amount ?? 0).toLocaleString()} ${pointsLabel}`;
}

function formatBookingAmount(amount: number | undefined, currency: string | undefined): string {
  if (!amount || amount <= 0) return '';
  const cur = (currency || 'USD').toUpperCase();
  const minDigits = BOOKING_ZERO_DECIMAL_CCY.has(cur) ? 0 : 2;
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: cur,
      minimumFractionDigits: minDigits,
      maximumFractionDigits: minDigits,
    }).format(amount);
  } catch {
    return `${amount} ${cur}`;
  }
}

/**
 * BookingFlow Component
 *
 * Manages the step-by-step booking process from date selection to form submission.
 */
const BookingFlow: React.FC<BookingFlowProps> = ({
  meetingType,
  username,
  onBookingComplete,
  mode = "booking",
  rescheduleToken,
  originalBookingData,
  onPreviewRedirect,
  serviceSelection,
  onBackToServiceFlow,
  previewRealMeetingTypeId,
}) => {
  // Define booking steps
  const STEPS = useMemo(
    () => ({
      CALENDAR: 0,
      TIME_SLOT: 1,
      BOOKING_FORM: 2,
    }),
    []
  );

  const [currentStep, setCurrentStep] = useState(STEPS.CALENDAR);
  const [isAnimating, setIsAnimating] = useState(false);
  const [animationDirection, setAnimationDirection] = useState("forward");
  const [selectedDuration, setSelectedDuration] = useState<number | null>(null);
  const [hostProfile, setHostProfile] = useState<any>(null);

  const { t, i18n } = useTranslation();

  // Use a ref to track which months have been prefetched to prevent duplicate requests
  const prefetchedMonths = useRef<Set<string>>(new Set());

  // Track initial mount to skip redundant prefetch (BookingDataProvider batch handles first load)
  const isInitialMount = useRef(true);

  // Use a ref to track if we've already set the language to prevent multiple overrides
  const hasSetLanguageRef = useRef(false);

  // Track if we've ever visited the TIME_SLOT step (to prevent re-animation on back/forward)
  const hasVisitedTimeSlotRef = useRef(false);

  // Override display language if meeting type has a specific language set
  // Only runs ONCE when component mounts or when displayLanguage changes
  // In preview mode, reset the ref to allow language changes
  useEffect(() => {
    // Reset the language ref in preview mode to allow updates
    if (mode === 'preview') {
      hasSetLanguageRef.current = false;
    }

    if (meetingType?.displayLanguage && !hasSetLanguageRef.current) {
      // In preview mode, always override language without checking user preferences
      if (mode === 'preview') {
        i18n.changeLanguage(meetingType.displayLanguage);
        return; // Don't mark as set in preview mode to allow changes
      }

      // Check if user has manually selected a language preference
      const userSelectedLanguage = localStorage.getItem('i18nextLng');

      // Add a flag to track if user has actively changed language during this session
      const userChangedLanguage = sessionStorage.getItem('userChangedLanguage');

      // Only override if user hasn't manually selected a language AND hasn't changed it this session
      if (!userChangedLanguage && (!userSelectedLanguage || userSelectedLanguage === meetingType.displayLanguage)) {
        // Mark that we've set the language to prevent re-runs
        hasSetLanguageRef.current = true;

        // Override with meeting-specific language
        i18n.changeLanguage(meetingType.displayLanguage);
      } else {
        // Also mark as set so we don't try again
        hasSetLanguageRef.current = true;
      }
    }
  }, [meetingType?.displayLanguage, i18n, mode]); // Add mode to dependencies

  // Get values and functions from booking page context
  const {
    selectedDate,
    setSelectedDate,
    selectedTime,
    setSelectedTime,
    selectedTimezone,
    setSelectedTimezone,
    bookingForm,
    updateBookingForm,
    setBookingSuccess,
    setBookingDetails,
    setPaymentInfo,
    cachedMonthSlots,
    isSubmitting,
    bookingSuccess: submissionBookingSuccess,
    bookingDetails: submissionBookingDetails,
    error: submissionError,
    setAvailableSlots,
    setLoadingSlots,
    setIsSubmitting,
    formTemplate,
  } = useBookingPage();

  // Get booking data provider for prefetching and cache management
  const { switchTimezone, slotsError, prefetchMonthSlots, clearCachedData, selectedProviderId } = useBookingData();

  // Slot unavailable banner state (rendered directly for correct fixed positioning)
  const [showSlotBanner, setShowSlotBanner] = useState(false);
  const [slotBannerMessage, setSlotBannerMessage] = useState('');

  /**
   * Bumped when the server refuses this booking for want of points, so the
   * entitlement guard opens its dialog. A counter, not a flag, so a second
   * refusal reopens it after the first was dismissed.
   */
  const [entitlementPromptToken, setEntitlementPromptToken] = useState(0);


  // Event waitlist claim: guest opened the page from a "spot held for you"
  // email (?waitlist_claim=token). While active, their reserved slot is
  // bookable even though it reads Full to everyone else.
  const [waitlistClaim, setWaitlistClaim] = useState<(WaitlistClaimInfo & { token: string }) | null>(null);
  const hasProcessedWaitlistParamsRef = useRef(false);

  // Event waitlist join: guest picked a FULL slot on a waitlist-enabled event.
  // The NORMAL booking form is reused — only the submit action changes
  // (join the waitlist instead of creating a booking).
  const [waitlistJoinMode, setWaitlistJoinMode] = useState(false);
  const [waitlistJoinedPosition, setWaitlistJoinedPosition] = useState<number | null>(null);

  // Get token from auth context
  const { token, user: hostUser } = useAuth();
  /** What this page actually locks on — see `meetingTypeAccess`. */
  const nonServiceAccessRequirement = effectiveAccessRequirement(meetingType);

  /**
   * The signed-in HOST is looking at their own booking link.
   *
   * Matched on the owner rather than on org membership, which the public
   * meeting-type payload does not carry. It under-triggers for a colleague,
   * which is the right way round: this turns on a warning only a host should
   * ever see, and showing it to a guest would be worse than a colleague
   * missing it.
   */
  const isPageOwner =
    !!hostUser?.id &&
    !!meetingType?.userId &&
    // The auth user's id is a STRING and the meeting type's is a NUMBER.
    // Compared directly these never match, and the warning would never appear.
    String(hostUser.id) === String(meetingType.userId);


  // Set host profile from meeting type user data (no separate API call needed)
  useEffect(() => {
    if (meetingType?.user) {
      setHostProfile(meetingType.user);
    } else {
      setHostProfile(null);
    }
  }, [meetingType?.user]);

  // (debug branding effect removed — was running on every meetingType change with Object.keys)

  // Initialize booking submission hook
  const {
    submitBooking,
    bookingSuccess: submissionSuccess,
    bookingDetails: submissionDetails,
    error: hookSubmissionError,
    clearError,
    paymentInfo: submissionPaymentInfo,
    planPurchase,
    clearPlanPurchase,
  } = useBookingSubmission({
    meetingType,
    selectedTime,
    selectedTimezone,
    token,
    selectedDate,
    mode,
    rescheduleToken,
    formTemplate,
    serviceSelection,
    waitlistClaimToken: waitlistClaim?.token || null,
    onBookingError: (error: string, enhancedError?: any) => {
      // CRITICAL: Always reset loading overlay on ANY error
      setIsSubmitting(false);

      /**
       * Refused for want of points, at the very last step.
       *
       * The option step checks this and normally stops someone long before
       * here, so reaching this point means the answer CHANGED underneath them —
       * the balance spent in another tab, or a session that expired. Rare, but
       * a bare "you do not have the points needed" with no way to get any is a
       * dead end, and they have a filled-in form to lose.
       *
       * The guard below is the same one from the option step, re-mounted here
       * and prodded open: it already knows how to sign someone in and how to
       * send them to the shop and back.
       */
      if (
        enhancedError?.errorCode === 'ENTITLEMENT_REQUIRED' ||
        enhancedError?.errorCode === 'ENTITLEMENT_SIGN_IN_REQUIRED'
      ) {
        clearError();
        setEntitlementPromptToken((n) => n + 1);
      }

      if (enhancedError?.shouldRefreshCache || enhancedError?.errorCode === 'SLOT_UNAVAILABLE') {
        // Clear inline error — the browser-top ErrorBanner handles UX for this case
        clearError();

        // Clear all cached availability data
        clearCachedData();
        prefetchedMonths.current.clear();

        // Show browser-top banner with slide-down animation
        // Rendered directly in the component tree (not via toast) so position:fixed
        // works correctly relative to the viewport
        setSlotBannerMessage(
          t(
            'booking:errors.slotUnavailableMessage',
            'This time slot was just booked by someone else. Please select another time.'
          )
        );
        setShowSlotBanner(true);

        // Go back to calendar/time slot selection
        setSelectedTime(null);
        if (currentStep === STEPS.BOOKING_FORM) {
          changeStep(STEPS.TIME_SLOT, 'backward');
        }

        // Trigger fresh availability refetch for current month
        const currentDuration = selectedDuration || meetingType?.duration?.[0] || 30;
        if (selectedDate) {
          prefetchMonthSlots(selectedDate, selectedTimezone, currentDuration).catch(() => {});
        }
      }
    }
  });

  // Monitor submission success and update app state
  useEffect(() => {
    if (submissionSuccess && submissionDetails) {
      setBookingSuccess(true);
      setBookingDetails(submissionDetails);
      onBookingComplete();
    }
  }, [
    submissionSuccess,
    submissionDetails,
    setBookingSuccess,
    setBookingDetails,
    onBookingComplete,
  ]);

  // Phase 3 — Pay Now: surface paymentInfo to context so BookingPageContent
  // can render PaymentScreen between the form and the confirmation page.
  useEffect(() => {
    if (submissionPaymentInfo) {
      setPaymentInfo(submissionPaymentInfo);
      if (submissionDetails) setBookingDetails(submissionDetails);
    }
  }, [submissionPaymentInfo, submissionDetails, setPaymentInfo, setBookingDetails]);

  // Initialize selected duration when meeting type loads
  // For reschedule, use original booking duration; for service bookings, use service duration
  useEffect(() => {
    if (meetingType && !selectedDuration) {
      // Priority: original booking duration (reschedule) > service duration > meeting type default
      if (mode === "reschedule" && originalBookingData?.duration) {
        setSelectedDuration(originalBookingData.duration);
      } else if (serviceSelection?.service?.durationMinutes) {
        // Service options mode: use service's duration
        setSelectedDuration(serviceSelection.service.durationMinutes);
      } else if (serviceSelection?.effectiveDuration) {
        // Duration mode: use the selected effectiveDuration
        setSelectedDuration(serviceSelection.effectiveDuration);
      } else {
        const durations = meetingType.duration;
        if (durations && durations.length > 0) {
          // Use the host-configured default duration when it's still a valid
          // option; otherwise fall back to the first duration.
          const configuredDefault = meetingType.defaultDuration;
          const initialDuration =
            configuredDefault != null && durations.includes(configuredDefault)
              ? configuredDefault
              : durations[0];
          setSelectedDuration(initialDuration);
        }
      }
    }
  }, [meetingType, selectedDuration, serviceSelection, mode, originalBookingData]);

  // Phase 2 payment — amount due for a non-service paid meeting type is the
  // price configured for the duration the invitee selected (per-duration
  // pricing). 0 when payment is off, service-type, or the duration has no price.
  // Priced in POINTS rather than money: the same per-duration map, a different
  // unit. Nothing is charged, so every amount shown is a point count.
  const pricedInPoints =
    meetingType?.paymentSettings?.enabled === true &&
    (meetingType?.paymentSettings as any)?.priceUnit === 'points';
  const pointsLabel = t('booking:points.unit', 'points');

  const paymentAmountDue =
    meetingType?.type !== 'service' && meetingType?.paymentSettings?.enabled
      ? Number(
          meetingType.paymentSettings.flatPrices?.[
            String(selectedDuration || meetingType.duration?.[0])
          ]
        )
      : 0;

  // Pre-populate form data for reschedule mode
  // Uses formSubmissionData (original form field values keyed by field ID) when available,
  // so all fields including custom ones are restored. Falls back to basic guest info.
  const hasPrePopulatedRef = useRef(false);
  useEffect(() => {
    if (mode !== "reschedule" || !originalBookingData || hasPrePopulatedRef.current) return;

    // If we have formSubmissionData from the original booking, use it directly
    // This preserves ALL form values including custom fields, keyed by field ID
    if (originalBookingData.formSubmissionData && Object.keys(originalBookingData.formSubmissionData).length > 0) {
      hasPrePopulatedRef.current = true;
      Object.entries(originalBookingData.formSubmissionData).forEach(([fieldId, value]) => {
        updateBookingForm(fieldId, value);
      });
    } else if (formTemplate?.fields) {
      // Fallback: map guestName/guestEmail to form template field IDs using systemFieldKey
      hasPrePopulatedRef.current = true;
      const { guestName, guestEmail, notes } = originalBookingData;

      formTemplate.fields.forEach((field) => {
        if (!field.isSystemField || !field.systemFieldKey) return;

        if (field.systemFieldKey === 'firstName' && guestName) {
          // Split combined name: for Western "First Last", take first word
          const parts = guestName.trim().split(/\s+/);
          updateBookingForm(field.id, parts[0] || '');
        } else if (field.systemFieldKey === 'lastName' && guestName) {
          const parts = guestName.trim().split(/\s+/);
          updateBookingForm(field.id, parts.slice(1).join(' ') || '');
        } else if (field.systemFieldKey === 'email' && guestEmail) {
          updateBookingForm(field.id, guestEmail);
        } else if (field.systemFieldKey === 'notes' && notes) {
          updateBookingForm(field.id, notes);
        }
      });
    }

    // Set timezone if different from current
    if (
      originalBookingData.timeZone &&
      originalBookingData.timeZone !== selectedTimezone
    ) {
      setSelectedTimezone(originalBookingData.timeZone);
    }
  }, [
    mode,
    originalBookingData,
    formTemplate,
    updateBookingForm,
    selectedTimezone,
    setSelectedTimezone,
  ]);

  // Prefetch on dependency change (timezone switch, etc.) — NOT on initial mount.
  // BookingDataProvider's batch auto-prefetch handles the first load via the batch endpoint,
  // so we skip the initial mount to avoid a duplicate /available-slots call.
  useEffect(() => {
    if (!meetingType?.id || meetingType.id === -1) return;

    // Skip initial mount — BookingDataProvider auto-prefetch handles first load via batch
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }

    const today = new Date();
    today.setHours(12, 0, 0, 0); // noon to avoid timezone issues

    const currentMonth = today.getMonth();
    const currentYear = today.getFullYear();

    // Create key for tracking prefetched months using consistent generateCacheKey
    const currentDuration =
      selectedDuration || meetingType?.duration?.[0] || 30;
    const currentMonthKey = generateCacheKey(
      meetingType.id,
      currentYear,
      currentMonth + 1,
      selectedTimezone,
      currentDuration,
      {
        bufferTimeBefore: meetingType.bufferTimeBefore,
        bufferTimeAfter: meetingType.bufferTimeAfter,
        slotIncrementMinutes: meetingType.slotIncrementMinutes,
        minimumBookingNotice: meetingType.minimumBookingNotice,
        maximumBookingWindow: meetingType.maximumBookingWindow,
      },
      meetingType.scheduleId,
      selectedProviderId
    );

    // Check if we've already prefetched this month
    if (!prefetchedMonths.current.has(currentMonthKey)) {
      // Mark as prefetched before the async call to prevent duplicate requests
      prefetchedMonths.current.add(currentMonthKey);

      const apiTimezone = selectedTimezone;

      prefetchMonthSlots(today, apiTimezone, currentDuration)
        .catch(() => {
          // Remove from prefetched set if it fails, so we can try again
          prefetchedMonths.current.delete(currentMonthKey);
        });
    }
  }, [
    meetingType?.id,
    meetingType?.timeZone,
    selectedTimezone,
    prefetchMonthSlots,
  ]);

  // Helper to animate step transitions - need to define this before using it in useEffect
  const changeStep = useCallback(
    (newStep: number, direction: "forward" | "backward") => {
      if (currentStep === newStep) return;

      // Set animation direction and change step immediately
      // Animation will play on the new content as it slides in
      setAnimationDirection(direction);
      setCurrentStep(newStep);
    },
    [currentStep]
  );

  // Mark TIME_SLOT as visited when we reach it
  useEffect(() => {
    if (currentStep === STEPS.TIME_SLOT && !hasVisitedTimeSlotRef.current) {
      hasVisitedTimeSlotRef.current = true;
    }
  }, [currentStep, STEPS.TIME_SLOT]);

  // Event waitlist: process ?waitlist_claim / ?waitlist_left URL params (once).
  // waitlist_claim → resolve the token; if the claim is active for THIS meeting
  // type, preselect the reserved slot's date, highlight the slot, and prefill
  // the guest's details. waitlist_left → confirmation banner after the guest
  // used the "leave the waitlist" email link.
  useEffect(() => {
    if (mode !== "booking" || !meetingType?.id || meetingType.id === -1) return;
    if (hasProcessedWaitlistParamsRef.current) return;
    hasProcessedWaitlistParamsRef.current = true;

    const params = new URLSearchParams(window.location.search);

    if (params.get("waitlist_left") === "1") {
      setSlotBannerMessage(
        t("booking:waitlist.leftConfirmation", "You have been removed from the waitlist.")
      );
      setShowSlotBanner(true);
      return;
    }

    const claimToken = params.get("waitlist_claim");
    if (!claimToken) return;

    bookingService.resolveWaitlistClaim(claimToken).then((info) => {
      if (!info || info.meetingTypeId !== meetingType.id) return;

      if (!info.claimActive) {
        // Booked already? Say nothing. Expired/promoted-past? Explain.
        if (info.status !== "booked") {
          setSlotBannerMessage(
            t(
              "booking:waitlist.claimExpired",
              "Your reserved spot has expired and was offered to the next person. You can rejoin the waitlist if the slot is still full."
            )
          );
          setShowSlotBanner(true);
        }
        return;
      }

      setWaitlistClaim({ ...info, token: claimToken });

      // Jump straight to the reserved slot's date
      const slotDate = new Date(info.slotStartTime);
      setSelectedDate(slotDate);
      changeStep(STEPS.TIME_SLOT, "forward");
    });
  }, [
    mode,
    meetingType?.id,
    t,
    setSelectedDate,
    changeStep,
    STEPS.TIME_SLOT,
  ]);

  // Prefill the claim guest's details once BOTH the claim and the form template
  // are available (the template often loads after the claim resolves). Same
  // systemFieldKey mapping the reschedule flow uses.
  const hasPrefilledClaimRef = useRef(false);
  useEffect(() => {
    if (!waitlistClaim || !formTemplate?.fields || hasPrefilledClaimRef.current) return;
    hasPrefilledClaimRef.current = true;

    const parts = waitlistClaim.guestName.trim().split(/\s+/);
    formTemplate.fields.forEach((field) => {
      if (!field.isSystemField || !field.systemFieldKey) return;
      if (field.systemFieldKey === "firstName") {
        updateBookingForm(field.id, parts[0] || "");
      } else if (field.systemFieldKey === "lastName") {
        updateBookingForm(field.id, parts.slice(1).join(" ") || "");
      } else if (field.systemFieldKey === "email") {
        updateBookingForm(field.id, waitlistClaim.guestEmail);
      } else if (field.systemFieldKey === "phone" && waitlistClaim.guestPhone) {
        updateBookingForm(field.id, waitlistClaim.guestPhone);
      }
    });
  }, [waitlistClaim, formTemplate, updateBookingForm]);

  // Auto-advance to time slot selection when a date is selected (disabled to prevent auto-selection)
  // useEffect(() => {
  //   if (selectedDate && currentStep === STEPS.CALENDAR) {
  //     changeStep(STEPS.TIME_SLOT, "forward");
  //   }
  // }, [selectedDate, STEPS.CALENDAR, STEPS.TIME_SLOT, changeStep, currentStep]);

  // Handle date selection
  const handleDateSelect = useCallback(
    (date: Date) => {
      // Immediate state update for responsive UI
      setSelectedDate(date);
      setSelectedTime(null);

      // Validation checks after state update (non-blocking for UI responsiveness)
      if (!meetingType) {
        return;
      }

      // Manually advance to time slot step when date is selected
      if (currentStep === STEPS.CALENDAR) {
        changeStep(STEPS.TIME_SLOT, "forward");
      }
    },
    [
      meetingType,
      currentStep,
      STEPS.CALENDAR,
      changeStep,
      setSelectedDate,
      setSelectedTime,
    ]
  );

  // Handle time slot selection - memoized
  const handleTimeSelect = useCallback(
    (time: TimeSlot) => {
      setWaitlistJoinMode(false); // Normal bookable slot picked
      setWaitlistJoinedPosition(null); // Clear any earlier waitlist confirmation
      setSelectedTime(time);
      setSelectedDate(new Date(time.start)); // Update selected date to match the time slot's day
      changeStep(STEPS.BOOKING_FORM, "forward");

      // Booking funnel step 2. Only a real, bookable slot on a real booking
      // page counts — preview and reschedule/cancel are excluded, and joining
      // a waitlist (handleWaitlistSelect) is deliberately not this step.
      if (mode === "booking") {
        trackBookingSlotSelected(meetingType?.id);
      }
    },
    [changeStep, STEPS.BOOKING_FORM, setSelectedDate, setSelectedTime, mode, meetingType?.id]
  );

  // Event waitlist: full slot picked on a waitlist-enabled event — same flow,
  // same form; only the submit action differs (join the waitlist)
  const handleWaitlistSelect = useCallback(
    (time: TimeSlot) => {
      setWaitlistJoinMode(true);
      setWaitlistJoinedPosition(null);
      setSelectedTime(time);
      setSelectedDate(new Date(time.start));
      changeStep(STEPS.BOOKING_FORM, "forward");
    },
    [changeStep, STEPS.BOOKING_FORM, setSelectedDate, setSelectedTime]
  );

  // Handle timezone change with non-blocking prefetching for better UX
  const handleTimezoneChange = useCallback(
    (timezone: string) => {
      // Step 1: Immediate UI updates (non-blocking)
      setSelectedTime(null);
      setSelectedTimezone(timezone);

      // Step 2: Background prefetching (non-blocking)
      if (meetingType?.id) {
        const currentDuration = selectedDuration || meetingType?.duration?.[0] || 30;

        // Run switchTimezone in background without blocking UI
        switchTimezone(timezone, currentDuration, selectedDate).catch(() => {
          // Error handling is managed by the provider
        });
      }
    },
    [setSelectedTimezone, setSelectedTime, switchTimezone, meetingType?.id, selectedDuration, selectedDate]
  );

  // Handle form submission - memoized
  const handleBookingSubmit = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();

      // Event waitlist: the SAME form submits a waitlist join instead of a
      // booking. Name/email/phone are extracted from the form exactly like a
      // booking submission; the guest fills the full form again when they
      // claim their spot (that claim becomes a real booking).
      if (waitlistJoinMode) {
        if (!meetingType || meetingType.id <= 0 || !selectedTime) return;

        const systemFields = extractSystemFields(bookingForm, formTemplate || null);
        setIsSubmitting(true);
        bookingService
          .joinWaitlist({
            meetingTypeId: String(meetingType.id),
            slotStartTime: new Date(selectedTime.start).toISOString(),
            slotEndTime: new Date(selectedTime.end).toISOString(),
            guestName: systemFields.name,
            guestEmail: systemFields.email,
            guestPhone: systemFields.phone || undefined,
            language: i18n.language || "en",
            timeZone: selectedTimezone,
          })
          .then(({ position }) => {
            setWaitlistJoinedPosition(position);
          })
          .catch((err: any) => {
            const message =
              err.code === "ALREADY_ON_WAITLIST"
                ? t("booking:waitlist.alreadyOnList", "You are already on the waitlist for this time slot.")
                : err.code === "WAITLIST_DISABLED"
                  ? t("booking:waitlist.disabled", "The waitlist is not available for this event.")
                  : t("booking:waitlist.joinError", "Failed to join the waitlist. Please try again.");
            setSlotBannerMessage(message);
            setShowSlotBanner(true);
          })
          .finally(() => setIsSubmitting(false));
        return;
      }

      // Show a full-page loading overlay when submitting
      setIsSubmitting(true);

      // Submit booking directly with all form data
      // The submission hook will extract system fields using formTemplate
      submitBooking(bookingForm);

      // We'll rely on the submitBooking function to handle success/error states
    },
    [
      bookingForm,
      submitBooking,
      setIsSubmitting,
      waitlistJoinMode,
      meetingType,
      selectedTime,
      formTemplate,
      selectedTimezone,
      i18n.language,
      t,
    ]
  );

  // Back navigation handlers
  const handleBackToCalendar = useCallback(() => {
    changeStep(STEPS.CALENDAR, "backward");
  }, [STEPS.CALENDAR, changeStep]);

  const handleBackToTimeSlot = useCallback(() => {
    setWaitlistJoinMode(false);
    setWaitlistJoinedPosition(null);
    changeStep(STEPS.TIME_SLOT, "backward");
  }, [STEPS.TIME_SLOT, changeStep]);

  // Calculate progress percentage
  const progressPercentage = useMemo(
    () => ((currentStep + 1) / Object.keys(STEPS).length) * 100,
    [currentStep, STEPS]
  );

  // Get step label based on current step
  const stepLabel = useMemo(() => {
    switch (currentStep) {
      case STEPS.CALENDAR:
        return t("booking:calendar.selectDate", "Select a date");
      case STEPS.TIME_SLOT:
        return t("booking:timeSlots.selectTime", "Select a time");
      case STEPS.BOOKING_FORM:
        return t("booking:form.title", "Complete booking");
      default:
        return t("booking:title", "Book appointment");
    }
  }, [currentStep, STEPS, t]);

  // Add a handler for month changes to prefetch data
  const handleMonthChange = useCallback(
    (date: Date) => {
      if (!meetingType?.id) return;

      // Validate the date - if it's invalid, don't try to fetch
      if (isNaN(date.getTime())) {
        return;
      }

      // We only prefetch if we don't already have this month cached
      const year = date.getFullYear();
      const month = date.getMonth() + 1;

      // Create a key for this month using consistent generateCacheKey
      const currentDuration =
        selectedDuration || meetingType?.duration?.[0] || 30;
      const monthKey = generateCacheKey(
        meetingType.id,
        year,
        month,
        selectedTimezone,
        currentDuration,
        {
          bufferTimeBefore: meetingType.bufferTimeBefore,
          bufferTimeAfter: meetingType.bufferTimeAfter,
          slotIncrementMinutes: meetingType.slotIncrementMinutes,
          minimumBookingNotice: meetingType.minimumBookingNotice,
          maximumBookingWindow: meetingType.maximumBookingWindow,
        },
        meetingType.scheduleId,
        selectedProviderId
      );

      // Check if we need to fetch:
      // 1. Not already marked as prefetched in our ref
      // 2. AND either no cache entry OR incomplete/errored cache entry
      // 3. OR cache is empty (handles duration switch case where cache was cleared)
      const needsFetch =
        !prefetchedMonths.current.has(monthKey) &&
        (!cachedMonthSlots[monthKey] ||
          !cachedMonthSlots[monthKey].isComplete ||
          cachedMonthSlots[monthKey].error ||
          !cachedMonthSlots[monthKey].slots ||
          Object.keys(cachedMonthSlots[monthKey].slots || {}).length === 0);

      if (needsFetch) {

        // Check if the date is in the future or past - avoid prefetching too far back
        const now = new Date();
        const sixMonthsAgo = new Date();
        sixMonthsAgo.setMonth(now.getMonth() - 6);

        const twoYearsAhead = new Date();
        twoYearsAhead.setFullYear(now.getFullYear() + 2);

        // Skip if the date is too far in the past or future
        if (date < sixMonthsAgo || date > twoYearsAhead) {
          return;
        }

        // Mark as prefetched before the async call to prevent duplicate requests
        prefetchedMonths.current.add(monthKey);

        // Use the viewer's selected timezone for API requests
        // The backend independently reads the host's timezone from the meeting type record
        const apiTimezone = selectedTimezone;

        // Prefetch the month data with proper error handling
        prefetchMonthSlots(date, apiTimezone, currentDuration)
          .catch(() => {
            // Remove from prefetched set if it fails, so we can try again later
            // But add a delay to prevent immediate retries
            setTimeout(() => {
              prefetchedMonths.current.delete(monthKey);
            }, 30000); // Longer delay (30s) to prevent rapid retries
          });
      } else if (
        cachedMonthSlots[monthKey] &&
        cachedMonthSlots[monthKey].error
      ) {
        // If we previously had an error for this month, don't retry too frequently
      }
    },
    [
      meetingType?.id,
      meetingType?.timeZone,
      meetingType,
      selectedTimezone,
      selectedDuration,
      selectedProviderId,
      prefetchMonthSlots,
      cachedMonthSlots,
    ]
  );

  /**
   * Release the "Scheduling your meeting…" overlay when the submit turns into a
   * checkout instead of a booking.
   *
   * That overlay is raised by `handleBookingSubmit` and normally lowered by the
   * success or error path. A plan checkout is neither, so without this it stayed
   * armed underneath — invisible while the checkout replaced the flow, and then
   * covering the form the moment someone backed out of subscribing.
   */
  useEffect(() => {
    if (planPurchase) setIsSubmitting(false);
  }, [planPurchase, setIsSubmitting]);

  /**
   * The plan checkout takes over the whole flow.
   *
   * They have already chosen the option, the person and the time, and the
   * server has told us the plan behind that option is not theirs yet. The only
   * thing left is to subscribe, so this replaces the flow rather than sitting
   * inside it — the calendar underneath would just be scenery.
   *
   * On success it re-submits the SAME form data. The booking was never created,
   * so there is nothing to reconcile: the second attempt is an ordinary booking
   * by someone who now holds the plan.
   */
  if (planPurchase && serviceSelection?.service) {
    return (
      <div className="h-full relative py-6">
        <PlanCheckoutScreen
          productId={planPurchase.productId}
          product={serviceSelection.service}
          email={extractSystemFields(bookingForm, formTemplate || null).email || ''}
          expiresAt={planPurchase.expiresAt}
          sellerName={
            meetingType?.user?.organization?.name || meetingType?.user?.name || null
          }
          /* Which wallet to read: a plan is held per organization, and holding
             one turns this screen from a sign-up into an upgrade. */
          organizationId={(meetingType as any)?.organizationId ?? null}
          onPurchased={async (outcome) => {
            /**
             * CONFIRM the booking that is already being held — never submit a
             * second one. Re-submitting was the old shape, and it raced: the
             * slot was on sale for the whole checkout, so a plan could be paid
             * for and the time gone by the time it completed.
             */
            const result = await bookingService.confirmPlanBooking(
              planPurchase.bookingId,
              getPurchaseGrant()
            );

            if (result.status === 'confirmed') {
              clearPlanPurchase();
              clearPurchaseGrant();

              /**
               * The confirmation page renders from `bookingDetails`, and
               * without it falls back to a bare "Booking Confirmed!" heading
               * with nothing underneath — which is exactly what a plan booking
               * used to land on. Built in the SAME shape the ordinary path
               * builds, so the two pages are the same page.
               */
              const confirmed = result.booking;
              if (confirmed) {
                setBookingDetails({
                  booking: {
                    ...confirmed,
                    host: confirmed.host || {
                      name: meetingType?.user?.name || 'Host',
                      email: meetingType?.user?.email,
                    },
                  },
                  meetingTitle: meetingType?.title || 'Meeting',
                  hostName:
                    confirmed.host?.name || meetingType?.user?.name || 'Host',
                  timezone: selectedTimezone,
                  // What they just joined, named on the confirmation.
                  bookedOptionName:
                    confirmed.product?.title || serviceSelection?.service?.title || null,
                  bookedOptionIsPlan:
                    (confirmed.product?.kind || serviceSelection?.service?.kind) ===
                    'subscription',
                  // An UPGRADE, and what the difference was. Said here because
                  // this is where they find out the change went through, and
                  // it arrives before the Stripe receipt does.
                  bookedOptionUpgraded: outcome?.upgraded === true,
                  bookedOptionUpgradeAmount: outcome?.amount ?? null,
                  bookedOptionUpgradeCurrency: outcome?.currency ?? null,
                });
              }

              setBookingSuccess(true);
              onBookingComplete();
              return;
            }

            // The plan was paid for either way, so say what is true and leave
            // them somewhere they can act.
            clearPlanPurchase();
            setSlotBannerMessage(
              result.message ||
                t(
                  'booking:planCheckout.confirmFailed',
                  'Your plan is active, but we could not confirm this time. Please pick another.'
                )
            );
            setShowSlotBanner(true);
            changeStep(STEPS.TIME_SLOT, "backward");
          }}
          onCancel={() => {
            clearPlanPurchase();
            changeStep(STEPS.BOOKING_FORM, "backward");
          }}
        />
      </div>
    );
  }

  return (
    <div className="h-full relative">
      {/* NON-SERVICE pages use the SAME guard as the service option step.
          There used to be a second component here, `EntitlementGate`, which
          drew its own panel at the top of the page and listed the point packs
          inline. Two components meant two copies of the sign-in, the shop
          route and the "what is on sale" rules, and they had already drifted:
          this one could not send anyone to the shop and did not know that
          buying happens there.

          ⚠️ IT ALSO NEVER OPENED ON A REFUSAL. `entitlementPromptToken` is
          bumped whenever the server refuses for want of points — but the guard
          below it was mounted only for service pages, so on a non-service
          points page the refusal opened nothing at all and the booking simply
          failed.

          `autoPromptWhenBlocked` because there is no option list here to be
          stopped at: the page itself is the first thing to stop someone at.

          ⚠️ NO SIGN-IN CONTAINER. `showSignInBanner` is off: the pop-up that
          opens on load already offers both doors — buy points, or sign in —
          and a panel underneath repeating the second one is the old
          `EntitlementGate` habit of asking a first-time visitor to log in to
          an account they have never had. The only banner left is the amber
          "you have N points" one, which is not a login prompt: it belongs to
          someone already signed in and short, and it is how they reopen the
          pop-up after dismissing it. */}
      {meetingType?.type !== 'service' && nonServiceAccessRequirement !== 'none' && (
        <ServiceEntitlementGuard
          meetingTypeId={meetingType?.id}
          organizationId={(meetingType as any)?.organizationId}
          username={username}
          accessRequirement={nonServiceAccessRequirement}
          selectedProductId={null}
          /**
           * ⚠️ NEVER null ON A PAGE PRICED PER DURATION.
           *
           * This resolves on load, before anyone has picked anything, and the
           * point cost is held per duration. Sending nothing made the server
           * resolve the cost to 0, which reads as unbookable — so a customer
           * with a full wallet was told they were short of points. The default
           * the page itself would book with is the honest stand-in until they
           * choose.
           */
          durationMinutes={
            selectedDuration ??
            (meetingType as any)?.defaultDuration ??
            (meetingType?.duration?.[0] ?? null)
          }
          requiresEntitlement
          autoPromptWhenBlocked
          showSignInBanner={false}
          isPageOwner={isPageOwner}
          onBlockedChange={() => {
            /* Reporting only — the server is what refuses a booking. */
          }}
          promptToken={entitlementPromptToken}
          bannerClassName="px-4 pt-4"
        />
      )}

      {/* The SAME guard as the option step, carried this far for one job: to
          explain a last-moment refusal and offer a way out of it. Its banners
          are off — the option step already said everything there is to say
          before they got here — so it is invisible until `promptToken` moves.
          Building a second dialog for this would have been two places to keep
          the sign-in and the shop route correct. */}
      {meetingType?.type === 'service' &&
        ((meetingType as any)?.accessRequirement ?? 'none') !== 'none' && (
          <ServiceEntitlementGuard
            meetingTypeId={meetingType?.id}
            organizationId={(meetingType as any)?.organizationId}
            username={username}
            accessRequirement={(meetingType as any)?.accessRequirement ?? 'none'}
            selectedProductId={serviceSelection?.service?.id ?? null}
            durationMinutes={selectedDuration ?? serviceSelection?.effectiveDuration ?? null}
            onBlockedChange={() => {
              /* Reporting only. The server has already refused; nothing here
                 gates the form a second time. */
            }}
            showSignInBanner={false}
            promptToken={entitlementPromptToken}
            selectedProductKind={serviceSelection?.service?.kind}
          />
        )}

      {/* Slot unavailable banner — rendered directly (not via toast) so
          position:fixed works relative to the viewport, not a transformed ancestor */}
      {showSlotBanner && (
        <ErrorBanner
          message={slotBannerMessage}
          visible={showSlotBanner}
          duration={5000}
          onDismiss={() => setShowSlotBanner(false)}
        />
      )}

      {/* Loading overlay for booking submission / waitlist join */}
      {isSubmitting && (
        <div className="loading-overlay fixed inset-0 z-50">
          <div className="bg-white p-8 rounded-lg shadow-lg flex flex-col items-center">
            <LoadingSpinner size="xl" color="primary" className="mb-4" />
            <h3 className="text-xl font-semibold text-secondary-800 mb-2">
              {waitlistJoinMode
                ? t("booking:waitlist.joining", "Joining...")
                : t("booking:status.scheduling", "Scheduling your meeting...")}
            </h3>
            <p className="text-secondary-600">
              {waitlistJoinMode
                ? t("booking:waitlist.joiningMessage", "Please wait while we add you to the waitlist.")
                : t(
                    "booking:status.schedulingMessage",
                    "Please wait while we confirm your booking and add it to the host's calendar."
                  )}
            </p>
          </div>
        </div>
      )}

      {currentStep === STEPS.BOOKING_FORM ? (
        // Two-column layout for booking form (responsive) - wrapped in single white container
        <div className="bg-white lg:rounded-lg lg:shadow-sm h-full">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 p-4 lg:p-6 h-full">
            {/* Meeting Details (Left) */}
            <div className="lg:col-span-4">
              <div className="h-full px-4 lg:px-0 lg:border-r lg:border-secondary-200 lg:pr-6">
                <div className="space-y-6">
                  {meetingType && (
                    <>
                      {/* For service bookings, show ServiceSummaryPanel instead of HostDisplay */}
                      {serviceSelection ? (
                        <ServiceSummaryPanel
                          meetingType={meetingType}
                          selectedService={serviceSelection.service}
                          selectedProvider={serviceSelection.provider}
                          isNoPreference={serviceSelection.isNoPreference}
                          effectiveDuration={serviceSelection.effectiveDuration}
                          effectivePrice={serviceSelection.effectivePrice}
                          showSocialLinks={true}
                          // For duration mode (no service selected): show duration and location
                          showDurationLocation={!serviceSelection.service}
                          availableDurations={meetingType.duration || []}
                          selectedDuration={serviceSelection.effectiveDuration}
                        />
                      ) : (
                        <>
                          {/* Social Media Position Helper */}
                          {(() => {
                            const socialMediaPosition = meetingType.hostDisplaySettings?.socialMediaDisplayPosition || 'below_avatar';

                            // Reusable SocialMediaLinks component
                            const renderSocialLinks = (additionalClassName?: string) => (
                              <SocialMediaLinks
                                twitterUrl={meetingType.twitterUrl}
                                facebookUrl={meetingType.facebookUrl}
                                instagramUrl={meetingType.instagramUrl}
                                linkedinUrl={meetingType.linkedinUrl}
                                youtubeUrl={meetingType.youtubeUrl}
                                lineUrl={meetingType.lineUrl}
                                websiteUrl={meetingType.websiteUrl}
                                socialMediaOrder={meetingType.socialMediaOrder}
                                className={additionalClassName || "mt-3"}
                              />
                            );

                            return (
                              <>
                                {/* Social Media Links - Above Avatar */}
                                {socialMediaPosition === 'above_avatar' && renderSocialLinks("mb-3")}

                                {/* Host Display - Above Title */}
                                <HostDisplay
                                  meetingType={meetingType}
                                  position="above_title"
                                />

                                {/* Social Media Links - Below Avatar (default) */}
                                {socialMediaPosition === 'below_avatar' && renderSocialLinks("mt-3")}

                                {/* Meeting Title - Made bigger */}
                                <div>
                                  <h3 className="text-2xl font-bold text-secondary-900">
                                    {meetingType?.title || "Meeting"}
                                  </h3>
                                  {/* Meeting Description - Moved closer to title */}
                                  {meetingType.description && (
                                    <p className="text-sm text-secondary-600 mt-2">
                                      {meetingType.description}
                                    </p>
                                  )}

                                  {/* Social Media Links - Below Description */}
                                  {socialMediaPosition === 'below_description' && renderSocialLinks("mt-4")}

                                  {/* Host Display - Below Description */}
                                  {(() => {
                                    const settings = meetingType.hostDisplaySettings || {
                                      showHostDetails: 'host' as const,
                                      displayPosition: 'above_title' as const
                                    };
                                    const shouldShow = settings.displayPosition === 'below_description' && settings.showHostDetails !== 'none';

                                    return shouldShow ? (
                                      <div className="py-4">
                                        <HostDisplay
                                          meetingType={meetingType}
                                          position="below_description"
                                        />
                                      </div>
                                    ) : null;
                                  })()}
                                </div>
                              </>
                            );
                          })()}
                        </>
                      )}

                      {/* Original time (strikethrough) + down arrow + New time for reschedule */}
                      {mode === "reschedule" && originalBookingData && selectedDate && selectedTime && (
                        <div className="flex items-start space-x-3">
                          <svg
                            className="h-5 w-5 mt-0.5"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                            style={{ color: 'var(--brand-color, #2E7AA0)' }}
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                            />
                          </svg>
                          <div>
                            {/* Original time - strikethrough */}
                            <p className="text-xs text-secondary-400 mb-0.5">
                              {t('booking:form.originalTime', 'Original Time')}
                            </p>
                            <p className="text-sm text-secondary-400 line-through">
                              {originalBookingData.startTime.toLocaleDateString(
                                i18n.language || "en",
                                {
                                  weekday: "long",
                                  month: "long",
                                  day: "numeric",
                                  timeZone: originalBookingData.timeZone,
                                }
                              )}
                            </p>
                            <p className="text-sm text-secondary-400 line-through">
                              {originalBookingData.startTime.toLocaleTimeString(
                                i18n.language || "en",
                                {
                                  hour: "numeric",
                                  minute: "2-digit",
                                  hour12: i18n.language !== 'ja',
                                  timeZone: originalBookingData.timeZone,
                                }
                              )}{" "}
                              -{" "}
                              {originalBookingData.endTime.toLocaleTimeString(
                                i18n.language || "en",
                                {
                                  hour: "numeric",
                                  minute: "2-digit",
                                  hour12: i18n.language !== 'ja',
                                  timeZone: originalBookingData.timeZone,
                                }
                              )}
                            </p>

                            {/* Down arrow */}
                            <div className="flex justify-center my-1">
                              <svg className="h-4 w-4 text-secondary-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
                              </svg>
                            </div>

                            {/* New time */}
                            <p className="text-xs text-secondary-500 mb-0.5">
                              {t('booking:form.newTime', 'New Time')}
                            </p>
                            <p className="text-sm font-medium text-secondary-900">
                              {selectedDate.toLocaleDateString(i18n.language || "en", {
                                weekday: "long",
                                month: "long",
                                day: "numeric",
                                timeZone: selectedTimezone,
                              })}
                            </p>
                            <p className="text-sm text-secondary-900">
                              {new Date(selectedTime.start).toLocaleTimeString(
                                i18n.language || "en",
                                {
                                  hour: "numeric",
                                  minute: "2-digit",
                                  hour12: i18n.language !== 'ja',
                                  timeZone: selectedTimezone,
                                }
                              )}{" "}
                              -{" "}
                              {new Date(selectedTime.end).toLocaleTimeString(
                                i18n.language || "en",
                                {
                                  hour: "numeric",
                                  minute: "2-digit",
                                  hour12: i18n.language !== 'ja',
                                  timeZone: selectedTimezone,
                                }
                              )}
                            </p>
                          </div>
                        </div>
                      )}

                      {/* Selected Time - normal booking mode */}
                      {mode !== "reschedule" && selectedDate && selectedTime && (
                        <div className="flex items-center space-x-3">
                          <svg
                            className="h-5 w-5"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                            style={{ color: 'var(--brand-color, #2E7AA0)' }}
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                            />
                          </svg>
                          <div>
                            <p className="text-xs text-secondary-500 mb-1">
                              {t('booking:form.time', 'Time')}
                            </p>
                            <p className="text-sm font-medium text-secondary-900">
                              {selectedDate.toLocaleDateString(i18n.language || "en", {
                                weekday: "long",
                                year: "numeric",
                                month: "long",
                                day: "numeric",
                                timeZone: selectedTimezone,
                              })}
                            </p>
                            <p className="text-sm text-secondary-900">
                              {new Date(selectedTime.start).toLocaleTimeString(
                                i18n.language || "en",
                                {
                                  hour: "numeric",
                                  minute: "2-digit",
                                  hour12: i18n.language !== 'ja',
                                  timeZone: selectedTimezone,
                                }
                              )}{" "}
                              -{" "}
                              {new Date(selectedTime.end).toLocaleTimeString(
                                i18n.language || "en",
                                {
                                  hour: "numeric",
                                  minute: "2-digit",
                                  hour12: i18n.language !== 'ja',
                                  timeZone: selectedTimezone,
                                }
                              )}
                            </p>
                          </div>
                        </div>
                      )}

                      {/* Location Info - Hide for duration mode service bookings (ServiceSummaryPanel handles it) */}
                      {!(serviceSelection && !serviceSelection.service) && (
                        <div className="flex items-center space-x-3">
                          <svg
                            className="h-5 w-5"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                            style={{ color: 'var(--brand-color, #2E7AA0)' }}
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                            />
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                            />
                          </svg>
                          <div>
                            <p className="text-xs text-secondary-500 mb-1">
                              {t("booking:confirmation.location", "Location")}
                            </p>
                            <p className="text-sm font-medium text-secondary-900">
                              {(() => {
                                // Determine location type based on isVirtual and isInPerson flags
                                const isVirtual = meetingType.isVirtual !== false; // Default to true
                                const isInPerson = meetingType.isInPerson === true;
                                const isPhone = !isVirtual && !isInPerson;
                                const showAfterBooking = meetingType.showLocationAfterBooking === true;

                                // If location should be hidden, show type + message
                                if (showAfterBooking) {
                                  if (isVirtual) {
                                    return (
                                      <>
                                        <span className="font-medium">{t('booking:location.online', 'Online Meeting')}</span>
                                        <span className="font-normal italic text-secondary-500"> ({t('booking:location.detailsAfterBooking', 'details will be shared after booking')})</span>
                                      </>
                                    );
                                  } else if (isInPerson) {
                                    return (
                                      <>
                                        <span className="font-medium">{t('booking:location.inPerson', 'In-Person Meeting')}</span>
                                        <span className="font-normal italic text-secondary-500"> ({t('booking:location.addressAfterBooking', 'address will be shared after booking')})</span>
                                      </>
                                    );
                                  } else if (isPhone) {
                                    return (
                                      <>
                                        <span className="font-medium">{t('booking:location.phone', 'Phone Call')}</span>
                                        <span className="font-normal italic text-secondary-500"> ({t('booking:location.numberAfterBooking', 'number will be shared after booking')})</span>
                                      </>
                                    );
                                  }
                                }

                                // Otherwise show full details
                                if (isVirtual) {
                                  return "Online meeting";
                                } else if (isInPerson) {
                                  // Show address if available
                                  return meetingType.locationDetails || "In-person meeting";
                                } else if (isPhone) {
                                  // Show phone number if available
                                  const phone = meetingType.phoneNumber
                                    ? `${meetingType.countryCode || '+1'} ${meetingType.phoneNumber}`
                                    : "Phone call";
                                  return phone;
                                }

                                return "Online meeting"; // Fallback
                              })()}
                            </p>
                          </div>
                        </div>
                      )}

                      {/* Phase 2 — Amount due for the selected duration (skip for
                          service type — service flow already shows price). */}
                      {meetingType?.type !== 'service' &&
                        meetingType?.paymentSettings?.enabled &&
                        paymentAmountDue > 0 && (
                          <div className="flex items-center space-x-3">
                            <svg
                              className="h-5 w-5"
                              fill="none"
                              viewBox="0 0 24 24"
                              stroke="currentColor"
                              style={{ color: 'var(--brand-color, #2E7AA0)' }}
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z"
                              />
                            </svg>
                            <div>
                              <p className="text-xs text-secondary-500 mb-1">
                                {t('booking:meetingDetails.amountDue', 'Amount due')}
                              </p>
                              <p className="text-sm font-medium text-secondary-900">
                                {pricedInPoints
                                  ? formatBookingPoints(paymentAmountDue, pointsLabel)
                                  : formatBookingAmount(
                                      paymentAmountDue,
                                      meetingType.paymentSettings.flatCurrency
                                    )}
                                <span className="text-xs text-secondary-500 font-normal ml-2">
                                  {meetingType.paymentSettings.mode === 'pay_now'
                                    ? t('booking:meetingDetails.payNowHint', '(pay to confirm)')
                                    : meetingType.paymentSettings.mode === 'pay_after_meeting'
                                    ? t('booking:meetingDetails.payAfterMeetingHint', '(invoice after the meeting)')
                                    : t('booking:meetingDetails.payLaterHint', '(invoice after booking)')}
                                </span>
                              </p>
                            </div>
                          </div>
                        )}

                      {/* Host Display - End of Details (hide for service bookings) */}
                      {!serviceSelection && (
                        <div className="mt-auto pt-4">
                          <HostDisplay
                            meetingType={meetingType}
                            position="end_of_details"
                          />
                        </div>
                      )}

                      {/* Social Media Links - Bottom Position (for ALL meeting types including service) */}
                      {meetingType.hostDisplaySettings?.socialMediaDisplayPosition === 'bottom' && (
                        <SocialMediaLinks
                          twitterUrl={meetingType.twitterUrl}
                          facebookUrl={meetingType.facebookUrl}
                          instagramUrl={meetingType.instagramUrl}
                          linkedinUrl={meetingType.linkedinUrl}
                          youtubeUrl={meetingType.youtubeUrl}
                          lineUrl={meetingType.lineUrl}
                          websiteUrl={meetingType.websiteUrl}
                          socialMediaOrder={meetingType.socialMediaOrder}
                          className="mt-4"
                        />
                      )}

                      {/* Timezone selector for form page - only show when no time is selected */}
                      {!selectedTime && (
                        <div className="space-y-2 flex-shrink-0 relative">
                          <label
                            htmlFor="timezone-select-form"
                            className="block text-sm font-medium text-secondary-700"
                          >
                            {t("booking:form.timezone", "Timezone")}
                          </label>
                          <BorderlessTimezoneSelector
                            id="timezone-select-form"
                            value={selectedTimezone}
                            onChange={handleTimezoneChange}
                            placeholder={t('booking:navigation.searchTimezones', 'Search timezones...')}
                          />
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>
            </div>

            {/* Booking Form (Right) - Animated */}
            <div className={`lg:col-span-8 ${currentStep === STEPS.BOOKING_FORM ? 'animate-slide-in-up' : ''}`}>
                {waitlistJoinedPosition !== null ? (
                  // Waitlist join confirmation — replaces the form after a successful join
                  <div className="flex flex-col items-center justify-center text-center py-12 px-4">
                    <div
                      className="mb-4 h-12 w-12 rounded-full flex items-center justify-center"
                      style={{ backgroundColor: "var(--brand-color-very-light, #E8F4FA)" }}
                    >
                      <svg
                        className="h-6 w-6"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                        style={{ color: "var(--brand-color, #2E7AA0)" }}
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    <h3 className="text-xl font-semibold text-secondary-900 mb-2">
                      {t("booking:waitlist.joinedTitle", "You're on the waitlist!")}
                    </h3>
                    <p className="text-sm text-secondary-600 mb-1">
                      {t("booking:waitlist.joinedPosition", "Your position in line: #{{position}}", {
                        position: waitlistJoinedPosition,
                      })}
                    </p>
                    <p className="text-sm text-secondary-600 mb-6 max-w-md">
                      {t(
                        "booking:waitlist.joinedDescription",
                        "We've sent you a confirmation email. If a spot opens up, you'll receive a personal booking link with a deadline to claim it."
                      )}
                    </p>
                    <button
                      onClick={handleBackToTimeSlot}
                      className="px-4 py-2 rounded-lg text-sm font-medium text-white"
                      style={{ backgroundColor: "var(--brand-color, #2E7AA0)" }}
                    >
                      {t("booking:timeSlots.backToCalendar", "Calendar")}
                    </button>
                  </div>
                ) : (
                <BookingFormStep
                  bookingForm={bookingForm}
                  onInputChange={(field, value) =>
                    updateBookingForm(field, value)
                  }
                  onBack={handleBackToTimeSlot}
                  onSubmit={handleBookingSubmit}
                  isSubmitting={isSubmitting}
                  error={hookSubmissionError || submissionError}
                  selectedDate={selectedDate}
                  selectedTime={selectedTime}
                  selectedTimezone={selectedTimezone}
                  mode={mode}
                  waitlistMode={waitlistJoinMode}
                />
                )}
              </div>
            </div>
          </div>
      ) : (
        // Three-column layout for date/time selection (responsive - stacks on mobile) - wrapped in single white container
        <div className="bg-white lg:rounded-lg lg:shadow-sm relative">
          {/* Branding Sash - Only render when user data is loaded to prevent flash */}
          {meetingType?.user && (
            <BrandingSash hideBranding={meetingType.user.hideSailLabBranding || false} />
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-4 md:gap-6 p-4 lg:p-6 h-full">
            {/* Meeting Details (Left) - 30% */}
            <div className="md:col-span-2 lg:col-span-4 order-1 md:order-1">
              <div className="h-full px-4 lg:px-0 lg:border-r lg:border-secondary-200 lg:pr-6">
                <div className="space-y-6">
                  {meetingType && (
                    <>
                      {/* For service bookings, show ServiceSummaryPanel instead of HostDisplay */}
                      {serviceSelection ? (
                        <ServiceSummaryPanel
                          meetingType={meetingType}
                          selectedService={serviceSelection.service}
                          selectedProvider={serviceSelection.provider}
                          isNoPreference={serviceSelection.isNoPreference}
                          effectiveDuration={serviceSelection.effectiveDuration}
                          effectivePrice={serviceSelection.effectivePrice}
                          showSocialLinks={true}
                          // For duration mode (no service selected): show duration and location
                          showDurationLocation={!serviceSelection.service}
                          availableDurations={meetingType.duration || []}
                          selectedDuration={serviceSelection.effectiveDuration}
                        />
                      ) : (
                        <>
                          {/* Social Media Position Helper - Calendar/Time Selection Step */}
                          {(() => {
                            const socialMediaPosition = meetingType.hostDisplaySettings?.socialMediaDisplayPosition || 'below_avatar';

                            // Reusable SocialMediaLinks component
                            const renderSocialLinks = (additionalClassName?: string) => (
                              <SocialMediaLinks
                                twitterUrl={meetingType.twitterUrl}
                                facebookUrl={meetingType.facebookUrl}
                                instagramUrl={meetingType.instagramUrl}
                                linkedinUrl={meetingType.linkedinUrl}
                                youtubeUrl={meetingType.youtubeUrl}
                                lineUrl={meetingType.lineUrl}
                                websiteUrl={meetingType.websiteUrl}
                                socialMediaOrder={meetingType.socialMediaOrder}
                                className={additionalClassName || "mt-3"}
                              />
                            );

                            return (
                              <>
                                {/* Social Media Links - Above Avatar */}
                                {socialMediaPosition === 'above_avatar' && renderSocialLinks("mb-3")}

                                {/* Host Display - Above Title */}
                                <HostDisplay
                                  meetingType={meetingType}
                                  position="above_title"
                                />

                                {/* Social Media Links - Below Avatar (default) */}
                                {socialMediaPosition === 'below_avatar' && renderSocialLinks("mt-3")}

                                {/* Meeting Title - Made bigger */}
                                <div>
                                  <h3 className="text-2xl font-bold text-secondary-900">
                                    {meetingType?.title || "Meeting"}
                                  </h3>
                                  {/* Meeting Description - Moved closer to title */}
                                  {meetingType.description && (
                                    <p className="text-sm text-secondary-600 mt-2">
                                      {meetingType.description}
                                    </p>
                                  )}

                                  {/* Social Media Links - Below Description */}
                                  {socialMediaPosition === 'below_description' && renderSocialLinks("mt-4")}

                                  {/* Host Display - Below Description */}
                                  {(() => {
                                    const settings = meetingType.hostDisplaySettings || {
                                      showHostDetails: 'host' as const,
                                      displayPosition: 'above_title' as const
                                    };
                                    const shouldShow = settings.displayPosition === 'below_description' && settings.showHostDetails !== 'none';

                                    return shouldShow ? (
                                      <div className="py-4">
                                        <HostDisplay
                                          meetingType={meetingType}
                                          position="below_description"
                                        />
                                      </div>
                                    ) : null;
                                  })()}
                                </div>
                              </>
                            );
                          })()}
                        </>
                      )}

                      {/* Duration info - only for non-service bookings (service shows in ServiceSummaryPanel) */}
                      {!serviceSelection && (
                      <div className="flex items-start space-x-3">
                        <svg
                          className="h-5 w-5 mt-1"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                          style={{ color: 'var(--brand-color, #2E7AA0)' }}
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                          />
                        </svg>
                        <div className="flex-1">
                          <p className="text-xs text-secondary-500 mb-1">{t('booking:timeSlots.durationLabel', 'Duration')}</p>
                          <div className="text-sm font-medium text-secondary-900">
                            {(() => {
                              const availableDurations =
                                meetingType.duration || [30];
                              const currentDuration =
                                selectedDuration || availableDurations[0];

                              if (availableDurations.length > 1) {
                                return (
                                  <Dropdown
                                    value={
                                      (selectedDuration ||
                                        meetingType.duration[0]).toString()
                                    }
                                    onChange={async (value) => {
                                      const newDuration = parseInt(value as string);

                                      // Update state
                                      setSelectedDuration(newDuration);
                                      setSelectedTime(null);

                                      // Clear cache and refetch properly
                                      if (meetingType) {
                                        clearCachedData();
                                        prefetchedMonths.current.clear();

                                        // Prefetch multiple months like initial load
                                        const monthsToPreload = Math.ceil((meetingType.maximumBookingWindow || 60) / 30) + 1;
                                        const fetchPromises = [];
                                        const referenceDate = selectedDate || new Date();

                                        for (let i = 0; i < monthsToPreload; i++) {
                                          const monthToFetch = new Date(
                                            referenceDate.getFullYear(),
                                            referenceDate.getMonth() + i,
                                            1
                                          );
                                          fetchPromises.push(
                                            prefetchMonthSlots(
                                              monthToFetch,
                                              selectedTimezone,
                                              newDuration
                                            ).catch((err) => {
                                              // Prefetch failed for this month, continue with others
                                            })
                                          );
                                        }

                                        // Wait for all fetches to complete
                                        await Promise.allSettled(fetchPromises);
                                      }
                                    }}
                                    options={availableDurations.map(
                                      (duration: number) => {
                                        // Show the per-duration price beside each option in the
                                        // open menu only (menuBadge). Once selected, the trigger
                                        // shows just the duration — the amount is in "Amount due" below.
                                        const durationPrice =
                                          meetingType?.type !== 'service' &&
                                          meetingType?.paymentSettings?.enabled
                                            ? Number(
                                                meetingType.paymentSettings.flatPrices?.[
                                                  String(duration)
                                                ]
                                              )
                                            : 0;
                                        return {
                                          value: duration.toString(),
                                          label: duration >= 60
                                            ? `${Math.floor(duration / 60)} hr${
                                                duration > 60
                                                  ? ` ${duration % 60} min`
                                                  : ""
                                              }`
                                            : `${duration} min`,
                                          menuBadge: durationPrice > 0
                                            ? pricedInPoints
                                              ? formatBookingPoints(durationPrice, pointsLabel)
                                              : formatBookingAmount(
                                                  durationPrice,
                                                  meetingType.paymentSettings?.flatCurrency
                                                )
                                            : undefined,
                                        };
                                      }
                                    )}
                                    hideSelectedCheck
                                    className={`inline-block w-auto ${
                                      meetingType?.type !== 'service' &&
                                      meetingType?.paymentSettings?.enabled
                                        ? 'min-w-[180px]'
                                        : 'min-w-[120px]'
                                    }`}
                                  />
                                );
                              } else {
                                return (
                                  <span>
                                    {currentDuration >= 60
                                      ? `${Math.floor(
                                          currentDuration / 60
                                        )} hr${
                                          currentDuration > 60
                                            ? ` ${currentDuration % 60} min`
                                            : ""
                                        }`
                                      : `${currentDuration} min`}
                                  </span>
                                );
                              }
                            })()}
                          </div>
                        </div>
                      </div>
                      )}

                      {/* Location Info - Hide for duration mode service bookings (ServiceSummaryPanel handles it) */}
                      {!(serviceSelection && !serviceSelection.service) && (
                        <div className="flex items-center space-x-3">
                          <svg
                            className="h-5 w-5"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                            style={{ color: 'var(--brand-color, #2E7AA0)' }}
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                            />
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                            />
                          </svg>
                          <div>
                            <p className="text-xs text-secondary-500 mb-1">
                              {t("booking:confirmation.location", "Location")}
                            </p>
                            <p className="text-sm font-medium text-secondary-900">
                              {(() => {
                                // Determine location type based on isVirtual and isInPerson flags
                                const isVirtual = meetingType.isVirtual !== false; // Default to true
                                const isInPerson = meetingType.isInPerson === true;
                                const isPhone = !isVirtual && !isInPerson;
                                const showAfterBooking = meetingType.showLocationAfterBooking === true;

                                // If location should be hidden, show type + message
                                if (showAfterBooking) {
                                  if (isVirtual) {
                                    return (
                                      <>
                                        <span className="font-medium">{t('booking:location.online', 'Online Meeting')}</span>
                                        <span className="font-normal italic text-secondary-500"> ({t('booking:location.detailsAfterBooking', 'details will be shared after booking')})</span>
                                      </>
                                    );
                                  } else if (isInPerson) {
                                    return (
                                      <>
                                        <span className="font-medium">{t('booking:location.inPerson', 'In-Person Meeting')}</span>
                                        <span className="font-normal italic text-secondary-500"> ({t('booking:location.addressAfterBooking', 'address will be shared after booking')})</span>
                                      </>
                                    );
                                  } else if (isPhone) {
                                    return (
                                      <>
                                        <span className="font-medium">{t('booking:location.phone', 'Phone Call')}</span>
                                        <span className="font-normal italic text-secondary-500"> ({t('booking:location.numberAfterBooking', 'number will be shared after booking')})</span>
                                      </>
                                    );
                                  }
                                }

                                // Otherwise show full details
                                if (isVirtual) {
                                  return "Online meeting";
                                } else if (isInPerson) {
                                  // Show address if available
                                  return meetingType.locationDetails || "In-person meeting";
                                } else if (isPhone) {
                                  // Show phone number if available
                                  const phone = meetingType.phoneNumber
                                    ? `${meetingType.countryCode || '+1'} ${meetingType.phoneNumber}`
                                    : "Phone call";
                                  return phone;
                                }

                                return "Online meeting"; // Fallback
                              })()}
                            </p>
                          </div>
                        </div>
                      )}

                      {/* Phase 2 — Amount due for the selected duration (skip for
                          service type — service flow already shows price). */}
                      {meetingType?.type !== 'service' &&
                        meetingType?.paymentSettings?.enabled &&
                        paymentAmountDue > 0 && (
                          <div className="flex items-center space-x-3">
                            <svg
                              className="h-5 w-5"
                              fill="none"
                              viewBox="0 0 24 24"
                              stroke="currentColor"
                              style={{ color: 'var(--brand-color, #2E7AA0)' }}
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z"
                              />
                            </svg>
                            <div>
                              <p className="text-xs text-secondary-500 mb-1">
                                {t('booking:meetingDetails.amountDue', 'Amount due')}
                              </p>
                              <p className="text-sm font-medium text-secondary-900">
                                {pricedInPoints
                                  ? formatBookingPoints(paymentAmountDue, pointsLabel)
                                  : formatBookingAmount(
                                      paymentAmountDue,
                                      meetingType.paymentSettings.flatCurrency
                                    )}
                                <span className="text-xs text-secondary-500 font-normal ml-2">
                                  {meetingType.paymentSettings.mode === 'pay_now'
                                    ? t('booking:meetingDetails.payNowHint', '(pay to confirm)')
                                    : meetingType.paymentSettings.mode === 'pay_after_meeting'
                                    ? t('booking:meetingDetails.payAfterMeetingHint', '(invoice after the meeting)')
                                    : t('booking:meetingDetails.payLaterHint', '(invoice after booking)')}
                                </span>
                              </p>
                            </div>
                          </div>
                        )}

                      {/* Host Display - End of Details (hide for service bookings) */}
                      {!serviceSelection && (
                        <div className="mt-auto pt-4">
                          <HostDisplay
                            meetingType={meetingType}
                            position="end_of_details"
                          />
                        </div>
                      )}

                      {/* Social Media Links - Bottom Position (for ALL meeting types including service) */}
                      {meetingType.hostDisplaySettings?.socialMediaDisplayPosition === 'bottom' && (
                        <SocialMediaLinks
                          twitterUrl={meetingType.twitterUrl}
                          facebookUrl={meetingType.facebookUrl}
                          instagramUrl={meetingType.instagramUrl}
                          linkedinUrl={meetingType.linkedinUrl}
                          youtubeUrl={meetingType.youtubeUrl}
                          lineUrl={meetingType.lineUrl}
                          websiteUrl={meetingType.websiteUrl}
                          socialMediaOrder={meetingType.socialMediaOrder}
                          className="mt-4"
                        />
                      )}
                    </>
                  )}
                </div>
              </div>
            </div>

            {/* Calendar (Middle) - 42% - Hidden on mobile when time slots are shown */}
            <div
              className={`md:col-span-1 lg:col-span-5 order-2 md:order-2 ${
                currentStep === STEPS.TIME_SLOT ? "hidden md:block" : "block"
              }`}
            >
              <div className="h-full px-4 lg:px-0 flex flex-col overflow-visible">
                {/* Back button for service bookings */}
                {serviceSelection && onBackToServiceFlow && (
                  <button
                    onClick={onBackToServiceFlow}
                    className="inline-flex items-center text-sm text-secondary-600 hover:text-secondary-900 transition-colors mb-4 self-start"
                  >
                    <svg
                      className="h-4 w-4 mr-1"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M15 19l-7-7 7-7"
                      />
                    </svg>
                    {t('booking:serviceBooking.backToProvider', 'Back to Provider')}
                  </button>
                )}

                <h3 className="text-lg font-semibold text-secondary-900 mb-6">
                  {t("booking:calendar.selectDate", "Select a date")}
                </h3>
                <div className="flex-1 mb-4">
                  <CalendarStep
                    selectedDate={selectedDate}
                    onDateSelect={handleDateSelect}
                    selectedTimezone={selectedTimezone}
                    onTimezoneChange={handleTimezoneChange}
                    onMonthChange={handleMonthChange}
                    selectedDuration={selectedDuration}
                  />
                </div>

                {/* Timezone selector */}
                <div className="mt-4 flex-shrink-0 relative">
                  <label
                    htmlFor="timezone-select-calendar"
                    className="block text-sm font-medium text-secondary-700 mb-2"
                  >
                    {t("booking:form.timezone", "Timezone")}
                  </label>
                  <BorderlessTimezoneSelector
                    id="timezone-select-calendar"
                    value={selectedTimezone}
                    onChange={handleTimezoneChange}
                    placeholder={t('booking:navigation.searchTimezones', 'Search timezones...')}
                  />

                  {/* Error display with retry button */}
                  {slotsError && (
                    <div className="alert-danger mt-3">
                      <div className="flex items-center justify-between">
                        <p className="text-sm">{slotsError}</p>
                        <button
                          onClick={() => handleTimezoneChange(selectedTimezone)}
                          className="ml-3 btn-danger btn-sm"
                        >
                          Retry
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Available Time Slots (Right) - 28% - Full width on mobile */}
            <div
              className={`md:col-span-1 lg:col-span-3 order-3 md:order-3 ${
                currentStep === STEPS.TIME_SLOT
                  ? "md:col-span-1 col-span-1"
                  : "hidden md:block"
              }`}
            >
              {currentStep === STEPS.TIME_SLOT ? (
                <div className={`h-full flex flex-col relative ${!hasVisitedTimeSlotRef.current ? 'animate-slide-in-up' : ''}`}>
                  <div className="mb-6">
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="text-lg font-semibold text-secondary-900">
                        {t("booking:timeSlots.title", "Available Times")}
                      </h3>
                      {/* Back to calendar button - mobile only */}
                      <button
                        onClick={() => changeStep(STEPS.CALENDAR, "backward")}
                        className="md:hidden inline-flex items-center px-3 py-1 text-sm text-secondary-600 hover:text-secondary-900 transition-colors"
                      >
                        <svg
                          className="h-4 w-4 mr-1"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M15 19l-7-7 7-7"
                          />
                        </svg>
                        {t('booking:timeSlots.backToCalendar', 'Calendar')}
                      </button>
                    </div>
                    {selectedDate && (
                      <p className="text-sm text-secondary-600 mt-1">
                        {selectedDate.toLocaleDateString(i18n.language || "en", {
                          weekday: "long",
                          month: "long",
                          day: "numeric",
                          timeZone: selectedTimezone,
                        })}
                      </p>
                    )}
                  </div>

                  {/* Time slots with improved scrolling */}
                  <div className="h-[calc(100%-6rem)] overflow-hidden">
                    <div className="h-full">
                      <TimeSlotDisplay
                        key={`${selectedDate?.toISOString()}-${selectedTimezone}-${selectedDuration || meetingType?.duration?.[0] || 30}`}
                        selectedDate={selectedDate}
                        onTimeSelect={handleTimeSelect}
                        selectedTimezone={selectedTimezone}
                        meetingType={meetingType}
                        selectedDuration={selectedDuration}
                        previewRealMeetingTypeId={previewRealMeetingTypeId}
                        claimedSlot={
                          waitlistClaim
                            ? {
                                start: new Date(waitlistClaim.slotStartTime),
                                end: new Date(waitlistClaim.slotEndTime),
                              }
                            : null
                        }
                        onWaitlistSelect={handleWaitlistSelect}
                      />
                    </div>
                  </div>
                </div>
              ) : (
                <div className="h-full flex flex-col items-center justify-center text-secondary-400 p-4 md:p-6">
                  <svg
                    className="h-16 w-16 mb-4 text-secondary-300"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={1.5}
                      d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                  <p className="text-center text-base font-medium mb-1 text-secondary-600">
                    {t("booking:calendar.selectDate", "Select a date")}
                  </p>
                  <p className="text-center text-sm text-secondary-500">
                    {t(
                      "booking:timeSlots.selectDateMessage",
                      "Available times will appear here"
                    )}
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BookingFlow;
